import { useEffect, useRef, useState } from 'react'
import { Room } from 'livekit-client'
import { deviceLabel, selectableDevices, systemDefaultLabel } from '@/lib/devices'
import { useVoiceStore, type VoicePrefs } from '@/store/voice'
import { captureKey, keyLabel } from '@/lib/audio/ptt'
import { describeMediaError, isMissingDevice, mediaErrorDetails, mediaErrorHint } from '@/lib/mediaErrors'
import { desktop, desktopPlatform } from '@/lib/desktop'
import { cx } from '@/lib/cx'
import layer from './SettingsLayer.module.css'
import styles from './VoiceSection.module.css'
import { Select } from '../Select'

const KINDS: { kind: MediaDeviceKind; key: keyof VoicePrefs; label: string }[] = [
  { kind: 'audioinput', key: 'audioinput', label: 'Микрофон' },
  { kind: 'audiooutput', key: 'audiooutput', label: 'Динамики' },
  { kind: 'videoinput', key: 'videoinput', label: 'Камера' },
]

/** Настройки «Голос и видео»: устройства, проверка микрофона, встроенная обработка звука. */
export function VoiceSection() {
  const prefs = useVoiceStore((s) => s.prefs)
  const setPrefs = useVoiceStore((s) => s.setPrefs)
  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [level, setLevel] = useState(0)
  const [testing, setTesting] = useState(false)
  const [starting, setStarting] = useState(false)
  const [mic, setMic] = useState<{ text: string; hint: string | null; details: string } | null>(null)
  const [fellBack, setFellBack] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const transmitting = useVoiceStore((s) => s.transmitting)
  const rnnoise = useVoiceStore((s) => s.rnnoise)
  const connected = useVoiceStore((s) => s.connection?.status === 'connected')

  const assignKey = async () => {
    setCapturing(true)
    try {
      const code = await captureKey()
      if (code) await setPrefs({ pttKey: code })
    } finally {
      setCapturing(false)
    }
  }
  const test = useRef<{ stream: MediaStream; ctx: AudioContext; raf: number } | null>(null)

  /*
   * Разрешения спрашиваем порознь. Общий запрос «микрофон и камера» падает целиком, если камеры
   * нет или её занял Zoom, — и названия микрофонов так и не появляются, хотя доступ к ним дали.
   */
  const refresh = async (permissions: boolean) => {
    if (permissions) {
      for (const constraints of [{ audio: true }, { video: true }]) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          stream.getTracks().forEach((t) => t.stop())
        } catch {
          /* этот вид устройств недоступен — остальные всё равно покажем */
        }
      }
    }
    try {
      setDevices(await Room.getLocalDevices(undefined, false))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? `Устройства недоступны: ${err.message}` : 'Устройства недоступны')
      setDevices([])
    }
  }

  useEffect(() => {
    let cancelled = false
    const load = () => {
      Room.getLocalDevices(undefined, false)
        .then((list) => {
          if (!cancelled) setDevices(list)
        })
        .catch(() => {
          if (!cancelled) setDevices([])
        })
    }
    load()
    // наушники воткнули или вынули — список перечитываем сами
    navigator.mediaDevices.addEventListener('devicechange', load)
    return () => {
      cancelled = true
      navigator.mediaDevices.removeEventListener('devicechange', load)
    }
  }, [])

  const stopTest = () => {
    const t = test.current
    if (!t) return
    cancelAnimationFrame(t.raf)
    t.stream.getTracks().forEach((track) => track.stop())
    void t.ctx.close()
    test.current = null
    setTesting(false)
    setLevel(0)
  }

  useEffect(() => stopTest, [])

  /** Микрофон с запасным вариантом: выбранное устройство могло исчезнуть или сменить id. */
  const micStream = async (): Promise<MediaStream> => {
    if (prefs.audioinput) {
      try {
        setFellBack(false)
        return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: prefs.audioinput } } })
      } catch (err) {
        if (!isMissingDevice(err)) throw err
        setFellBack(true)
      }
    }
    return navigator.mediaDevices.getUserMedia({ audio: true })
  }

  const startTest = async () => {
    setStarting(true)
    setMic(null)
    try {
      const stream = await micStream()
      const ctx = new AudioContext()
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 512
      ctx.createMediaStreamSource(stream).connect(analyser)
      const data = new Uint8Array(analyser.fftSize)
      const tick = () => {
        analyser.getByteTimeDomainData(data)
        let sum = 0
        for (const v of data) sum += (v - 128) ** 2
        setLevel(Math.min(1, Math.sqrt(sum / data.length) / 40))
        if (test.current) test.current.raf = requestAnimationFrame(tick)
      }
      test.current = { stream, ctx, raf: requestAnimationFrame(tick) }
      setTesting(true)
      setError(null)
    } catch (err) {
      // ошибку показываем здесь же, у кнопки: раньше она уезжала в самый низ страницы
      setMic({ text: describeMediaError(err, 'mic', desktopPlatform), hint: mediaErrorHint(err, 'mic', desktopPlatform), details: mediaErrorDetails(err) })
    } finally {
      setStarting(false)
    }
  }

  const labelOf = deviceLabel
  const named = devices?.some((d) => d.label)

  return (
    <section>
      <h1 className={layer.sectionTitle}>Голос и видео</h1>
      <h2 className={layer.subTitle}>Устройства</h2>
      {devices && !named ? (
        <p className={layer.text}>
          Названия устройств появятся после разрешения на доступ к микрофону и камере.{' '}
          <button type="button" className={styles.link} onClick={() => void refresh(true)}>Разрешить</button>
        </p>
      ) : null}
      <div className={styles.devices}>
        {KINDS.map(({ kind, key, label }) => {
          const all = (devices ?? []).filter((d) => d.kind === kind)
          // без служебных «Default» и «Communications» Chrome; основное устройство системы — в строке «По умолчанию»
          const list = selectableDevices(all)
          const systemDefault = systemDefaultLabel(all)
          return (
            <label key={kind} className={styles.field}>
              <span className={styles.label}>{label}</span>
              <Select
                value={prefs[key] as string}
                options={[{ value: '', label: systemDefault ? `По умолчанию (${systemDefault})` : 'По умолчанию' }, ...list.map((d, i) => ({ value: d.deviceId, label: labelOf(d, i) }))]}
                onChange={(v) => void setPrefs({ [key]: v })}
              />
            </label>
          )
        })}
      </div>

      <h2 className={layer.subTitle}>Проверка микрофона</h2>
      <div className={styles.meterRow}>
        <button type="button" className={cx(layer.button, testing && layer.buttonSecondary)} disabled={starting} onClick={() => (testing ? stopTest() : void startTest())}>
          {starting ? 'Включаем…' : testing ? 'Остановить' : 'Проверить'}
        </button>
        <div className={styles.meter} role="meter" aria-label="Уровень микрофона" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)}>
          <div className={styles.meterFill} style={{ width: `${Math.round(level * 100)}%` }} />
        </div>
      </div>
      <p className={layer.text}>Скажите что-нибудь: полоса покажет уровень сигнала выбранного микрофона.</p>
      {fellBack && testing ? <p className={layer.text}>Выбранного микрофона больше нет — проверяем тот, что система считает основным.</p> : null}
      {mic ? (
        <>
          <p className={styles.error} role="alert">{mic.text}</p>
          {mic.hint ? <p className={layer.text}>{mic.hint}</p> : null}
          {desktop && (desktopPlatform === 'win32' || desktopPlatform === 'darwin') ? (
            <button type="button" className={layer.button} onClick={() => desktop?.openPrivacySettings('microphone')}>
              Открыть настройки доступа
            </button>
          ) : null}
          {/*
            * Техническая строка нужна только когда что-то сломалось, и по ней видно то,
            * чего не видно иначе: как система назвала отказ и какие устройства приложение
            * вообще различает. Свёрнуто, чтобы не мешать тем, у кого всё работает.
            */}
          <details className={styles.details}>
            <summary>Подробности — если понадобится помощь</summary>
            <p>{mic.details || 'система не назвала причину'}</p>
            <p>Выбрано: {prefs.audioinput ? (devices?.find((d) => d.deviceId === prefs.audioinput)?.label || prefs.audioinput) : 'по умолчанию'}</p>
            <p>
              Приложение видит микрофонов: {(devices ?? []).filter((d) => d.kind === 'audioinput').length}
              {named ? '' : ' (без названий — доступ ещё ни разу не выдавался)'}
            </p>
            {(devices ?? []).filter((d) => d.kind === 'audioinput').map((d, i) => (
              <p key={d.deviceId || i}>· {d.label || 'без названия'}</p>
            ))}
          </details>
        </>
      ) : null}

      <h2 className={layer.subTitle}>Режим активации микрофона</h2>
      <div className={styles.toggles} role="radiogroup" aria-label="Режим активации микрофона">
        {([
          ['voice', 'По голосу', 'Микрофон включается, когда вы говорите'],
          ['ptt', 'Push-to-talk', 'Голос передаётся, пока удерживаете клавишу'],
        ] as const).map(([mode, label, hint]) => (
          <label key={mode} className={styles.toggle}>
            <input type="radio" name="inputMode" value={mode} checked={prefs.inputMode === mode} onChange={() => void setPrefs({ inputMode: mode })} />
            <span className={styles.toggleText}>
              <span className={styles.toggleLabel}>{label}</span>
              <span className={styles.toggleHint}>{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {prefs.inputMode === 'voice' ? (
        <div className={styles.toggles}>
          <label className={styles.toggle}>
            <input type="checkbox" checked={prefs.vadAuto} onChange={(e) => void setPrefs({ vadAuto: e.target.checked })} />
            <span className={styles.toggleText}>
              <span className={styles.toggleLabel}>Определять чувствительность автоматически</span>
              <span className={styles.toggleHint}>Порог подстраивается под уровень шума; с RNNoise учитывается детектор речи</span>
            </span>
          </label>
          {!prefs.vadAuto ? (
            <label className={styles.field}>
              <span className={styles.label}>Порог: {prefs.vadThreshold} дБ</span>
              <input type="range" min={-100} max={0} step={1} value={prefs.vadThreshold} className={styles.range} aria-label="Порог активации, дБ" onChange={(e) => void setPrefs({ vadThreshold: Number(e.target.value) })} />
            </label>
          ) : null}
        </div>
      ) : (
        <div className={styles.meterRow}>
          <span className={styles.keyLabel}>Клавиша: <kbd className={styles.kbd}>{keyLabel(prefs.pttKey)}</kbd></span>
          <button type="button" className={cx(layer.button, capturing && layer.buttonSecondary)} onClick={() => void assignKey()} disabled={capturing}>
            {capturing ? 'Нажмите клавишу, среднюю или боковую кнопку мыши… (Esc — отмена)' : 'Назначить клавишу'}
          </button>
        </div>
      )}
      {prefs.inputMode === 'ptt' ? <p className={layer.text}>Клавиша работает, пока окно «Кличат» в фокусе, и не перехватывается в полях ввода; глобальная клавиша поверх игр работает в десктоп-приложении.</p> : null}
      {connected ? <p className={layer.text}>Сейчас {transmitting ? 'передаём голос' : 'тишина — голос не передаётся'}.</p> : null}

      <h2 className={layer.subTitle}>Обработка звука</h2>
      <div className={styles.toggles}>
        {([
          ['rnnoise', 'Шумоподавление RNNoise', 'Шумоподавление с помощью нейросети. Выключено — звук как он есть'],
          ['echoCancellation', 'Подавление эха', 'Нужно, если говорите через динамики'],
          ['autoGainControl', 'Автоматическая громкость', 'Выравнивает уровень микрофона'],
          ['duckStream', 'Приглушать чужую трансляцию, пока говорю', 'Обычно не нужно: показывающий отдаёт звук уже без наших голосов. Включите, если слышите себя эхом'],
          ['stripVoices', 'Убирать голоса собеседников из своей трансляции', 'Захват звука системы берёт и то, что играет Кличат: вычитаем его, чтобы зрители не слышали самих себя'],
          ['startMuted', 'Входить с выключенным микрофоном', 'Микрофон включается кнопкой уже в канале'],
          ['sounds', 'Звуки событий', 'Подключение и выход, микрофон, демонстрация экрана, камера'],
        ] as const).map(([key, label, hint]) => (
          <label key={key} className={styles.toggle}>
            <input
              type="checkbox"
              checked={Boolean(prefs[key])}
              // RNNoise идёт вместе со встроенным шумоподавлением браузера: отдельной галочки у того больше нет
              onChange={(e) => void setPrefs(key === 'rnnoise' ? { rnnoise: e.target.checked, noiseSuppression: e.target.checked } : { [key]: e.target.checked })}
            />
            <span className={styles.toggleText}>
              <span className={styles.toggleLabel}>{label}</span>
              <span className={styles.toggleHint}>{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {connected && prefs.rnnoise ? (
        <p className={layer.text}>
          {rnnoise === false
            ? 'RNNoise не запустился — голос идёт без нейросетевого шумоподавления.'
            : rnnoise
              ? 'RNNoise работает: фон вычитается из вашего голоса.'
              : 'RNNoise загружается…'}
        </p>
      ) : null}
      <p className={layer.text}>Применяется сразу. Переключение обработки перезапускает микрофон, будет короткий разрыв.</p>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
    </section>
  )
}
